import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Interfaces compatible with our new architecture
interface Term { id: number; name: string; is_active: boolean; academic_year_name: string; }
interface SchoolClass { id: number; name: string; }


const Marks: React.FC = () => {
  // 1. Selection State
  const [terms, setTerms] = useState<Term[]>([]);
  const [selectedTerm, setSelectedTerm] = useState<number | null>(null);

  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);

  // 2. Data State for Broadsheet
  const [broadsheetData, setBroadsheetData] = useState<any[]>([]); // Pivoted data
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial Load: Fetch Terms, Classes and Subjects
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch Terms
        const termsRes = await axios.get('/exams/terms/');
        const termsData = termsRes.data.results || termsRes.data;
        setTerms(termsData);
        const active = termsData.find((t: Term) => t.is_active);
        if (active) setSelectedTerm(active.id);

        // Fetch Classes logic
        try {
          // 1. Fetch Current User to check Role & Assigned Classes
          let userRole = '';
          let assignedClassIds: number[] = [];
          try {
            const userRes = await axios.get('/users/user');
            const userData = userRes.data.user;
            userRole = userData.role;
            if (userData.assigned_classes) {
              assignedClassIds = userData.assigned_classes.map((c: any) => c.id);
            }
          } catch (uErr) { console.error(uErr); userRole = localStorage.getItem('role') || ''; }

          const classesRes = await axios.get('/Branches/classes/');
          let classList: SchoolClass[] = Array.isArray(classesRes.data) ? classesRes.data : (classesRes.data.results || []);

          // 3. Filter if Teacher
          if (userRole === 'teacher' && assignedClassIds.length > 0) {
            classList = classList.filter(c => assignedClassIds.includes(c.id));
          } else if (userRole === 'teacher' && assignedClassIds.length === 0) {
            classList = [];
            toast.warn("You are not assigned to any classes.");
          }
          setClasses(classList);
        } catch (err) { console.error(err); }

        // Fetch Subjects (Columns)
        const subjectsRes = await axios.get('/exams/subjects/');
        setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : (subjectsRes.data.results || []));

      } catch (err) { console.error(err); }
    };

    fetchInitialData();
  }, []);

  // Fetch RAW SCORES and Pivot them
  const fetchResults = async () => {
    if (!selectedTerm || !selectedClass) {
      toast.warn("Please select a Term and Class.");
      return;
    }

    setLoading(true);
    setError(null);
    setBroadsheetData([]); // Clear previous

    try {
      // 1. Fetch Students in Class
      const studentsRes = await axios.get(`/Branches/students/?class_id=${selectedClass}`);
      const students = Array.isArray(studentsRes.data) ? studentsRes.data : (studentsRes.data.results || []);

      // 2. Fetch ALL Scores for this Term & Class
      // We use the new filtered endpoint we created in backend
      const scoresRes = await axios.get(`/exams/assessment-scores/?term_id=${selectedTerm}&class_id=${selectedClass}`);
      const scores = Array.isArray(scoresRes.data) ? scoresRes.data : (scoresRes.data.results || []);

      // 3. Pivot Data: Student -> Subjects -> { cat, final, total }
      const pivoted: any = {};

      // Initialize students
      students.forEach((s: any) => {
        pivoted[s.id] = {
          id: s.id,
          name: s.name || 'Unknown',
          subjects: {}, // { subId: { cat: 0, final: 0, total: 0 } }
          total_score: 0
        };
      });

      // Populate scores
      scores.forEach((score: any) => {
        const sId = score.student;
        const subId = score.subject;
        const type = score.assessment_type_code; // CAT or FINAL (requires backend update)
        const val = parseFloat(score.score);

        if (pivoted[sId]) {
          if (!pivoted[sId].subjects[subId]) {
            pivoted[sId].subjects[subId] = { cat: 0, final: 0, total: 0 };
          }

          // Accumulate based on type
          if (type === 'CAT') pivoted[sId].subjects[subId].cat += val;
          else if (type === 'FINAL') pivoted[sId].subjects[subId].final += val;

          // Always add to total for that subject
          pivoted[sId].subjects[subId].total += val;

          // Student Grand Total
          pivoted[sId].total_score += val;
        }
      });

      // Convert back to array and sort
      const resultList = Object.values(pivoted).sort((a: any, b: any) => b.total_score - a.total_score);

      // Assign positions
      resultList.forEach((r: any, idx: number) => r.position = idx + 1);

      setBroadsheetData(resultList);

      if (resultList.length === 0) toast.info("No students or scores found.");

    } catch (err) {
      console.error("Failed to fetch broadsheet", err);
      setError("Failed to load broadsheet.");
      toast.error("Failed to load broadsheet.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-content">
      <ToastContainer />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Class Results</h2>
          <p className="text-muted mb-0">View student performance summary (CAT + Final).</p>
        </div>
      </div>

      {/* Selection Panel */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-4">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label fw-bold small text-secondary">Term</label>
              <select className="form-select" value={selectedTerm || ''} onChange={e => setSelectedTerm(Number(e.target.value))}>
                <option value="">Select Term...</option>
                {terms?.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.academic_year_name}) {t.is_active ? '✅' : ''}</option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold small text-secondary">Class</label>
              <select className="form-select" value={selectedClass || ''} onChange={e => setSelectedClass(Number(e.target.value))}>
                <option value="">Select Class...</option>
                {classes?.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <button className="btn btn-primary w-100 fw-bold" onClick={fetchResults} disabled={loading || !selectedTerm || !selectedClass}>
                {loading ? (
                  <span><span className="spinner-border spinner-border-sm me-2" />Loading...</span>
                ) : (
                  'View Results'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Broadsheet Table */}
      {error && (
        <div className="alert alert-warning border-0 shadow-sm mb-4">
          <div className="d-flex align-items-center">
            <i className="fas fa-exclamation-triangle me-3 fs-4"></i>
            <div>{error}</div>
          </div>
        </div>
      )}

      {broadsheetData.length > 0 && (
        <div className="card shadow border-0">
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-bold text-primary">
              <i className="fas fa-table me-2"></i> Class Broadsheet
            </h6>
            <span className="badge bg-light text-secondary">{broadsheetData.length} Students</span>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered table-striped table-hover mb-0 align-middle small">
              <thead className="bg-light text-uppercase fw-bold text-center">
                <tr>
                  <th className="bg-light sticky-col first-col" style={{ position: 'sticky', left: 0, zIndex: 10, minWidth: '50px' }}>Pos</th>
                  <th className="bg-light sticky-col second-col text-start ps-3" style={{ position: 'sticky', left: '50px', zIndex: 10, minWidth: '200px' }}>Student Name</th>
                  {subjects.map((sub: any) => (
                    <th key={sub.id} style={{ minWidth: '100px' }}>{sub.name.substring(0, 3)}</th>
                  ))}
                  <th className="bg-light ps-3 text-primary">Total</th>
                </tr>
              </thead>
              <tbody>
                {broadsheetData.map((student) => (
                  <tr key={student.id}>
                    <td className="text-center fw-bold sticky-col first-col bg-white" style={{ position: 'sticky', left: 0, zIndex: 5 }}>
                      {student.position}
                    </td>
                    <td className="fw-medium sticky-col second-col bg-white ps-3" style={{ position: 'sticky', left: '50px', zIndex: 5 }}>
                      {student.name}
                    </td>
                    {subjects.map((sub: any) => {
                      const sData = student.subjects[sub.id];
                      return (
                        <td key={sub.id} className="text-center">
                          {sData ? (
                            <div>
                              <div className="fw-bold text-dark">{sData.total?.toFixed(0)}</div>
                              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                C:{sData.cat || 0} F:{sData.final || 0}
                              </div>
                            </div>
                          ) : '-'}
                        </td>
                      );
                    })}
                    <td className="text-center fw-bold text-primary fs-6">{student.total_score.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {broadsheetData.length === 0 && !loading && !error && (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-clipboard-list fs-1 mb-3 opacity-25"></i>
          <p>Select a Term and Class to view the broadsheet.</p>
        </div>
      )}
    </div>
  );
};

export default Marks;